import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../auth/store';
import { usePermissions, Permission } from '../../../shared/hooks/usePermissions';
import Button from '../../../shared/components/Button';
import { useToast } from '../../../shared/components/toast';
import { useContentPages, useDeleteContentPage } from '../hooks/useContentPages';
import { ContentPagesTable } from '../components/ContentPagesTable';
import type { ContentPage } from '../types';

const emptyBox =
  'text-center py-16 text-muted-foreground text-[14px] border border-dashed border-[#D9D9D9] rounded-[16px]';

/**
 * CMS-lite pages & policies list (PE-2175). Gated on `selectedTenantId` exactly
 * like `CustomOrdersPage` — `/content-pages` has no cross-tenant view, so there is
 * nothing to show until a merchant is selected.
 */
export function ContentPagesPage() {
  const navigate = useNavigate();
  const selectedTenantId = useAuthStore((s) => s.selectedTenantId);
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.CONTENT_PAGE_MANAGE);
  const { toast } = useToast();

  const { data, isLoading, error } = useContentPages(selectedTenantId);
  const deletePage = useDeleteContentPage(selectedTenantId);

  const handleDelete = (page: ContentPage) => {
    deletePage.mutate(page.id, {
      onSuccess: () => toast.success('Page deleted'),
      // Non-401 failures are already toasted by ApiFetch — no double toast here.
    });
  };

  return (
    <div className="py-6 max-w-6xl mx-auto px-2">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-[23px] font-bold text-foreground">Pages & policies</h1>
          <p className="text-[14px] text-muted-foreground mt-1">
            Manage standalone storefront pages — FAQs, shipping/returns policies, terms, and more.
          </p>
        </div>
        {selectedTenantId && canManage && (
          <Button size="md" onClick={() => navigate('/content-pages/new')}>
            New page
          </Button>
        )}
      </div>

      {!selectedTenantId ? (
        <div className={emptyBox}>Select a merchant to manage their pages.</div>
      ) : isLoading ? (
        <div className="text-center py-16 text-muted-foreground">Loading pages…</div>
      ) : error ? (
        <div className="text-center py-16 text-red-500">Failed to load pages.</div>
      ) : (
        <ContentPagesTable
          pages={data?.data ?? []}
          isLoading={false}
          canManage={canManage}
          onDelete={handleDelete}
          isDeleting={deletePage.isPending}
        />
      )}
    </div>
  );
}
